import { useEffect, useRef, useState } from "react";
import { API_CHAT_URL, getWsUrl } from "../Api/apiconnector";

export default function ChatPopup({ role, onClose }) {
  const [messages, setMessages] = useState([]);
  const [faqs, setFaqs] = useState([]);
  const [text, setText] = useState("");
  const wsRef = useRef(null);
  const roomIdRef = useRef(null);
  const [socketReady, setSocketReady] = useState(false);
  const historyLoadedRef = useRef(false);
  const [expandedFaqs, setExpandedFaqs] = useState({});
  const messageEndRef = useRef(null);


  useEffect(() => {
    messageEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // ----------------------------------
  // INIT ROOM + WEBSOCKET
  // ----------------------------------
  useEffect(() => {
    if (wsRef.current) return; // 👈 PREVENT DOUBLE CONNECTION
    
    let ws;
    historyLoadedRef.current = false;
  
    fetch(`${API_CHAT_URL}/active-room/?role=${role}`, { credentials: "include" })
      .then(res => res.json())
      .then(room => {
        roomIdRef.current = room.id;

        // 🔥 SHOW FAQs ONLY FOR NEW ROOM
        // if ((role === "client" || role === "radiologist") && room.faqs?.length) {
        //   const faqMessages = room.faqs.flatMap(faq => ([
        //     { sender: "Bot", message: `❓ ${faq.question}` },
        //     { sender: "Bot", message: `💡 ${faq.answer}` },
        //   ]));
        //   setMessages(faqMessages);
        // }

        if ((role === "client" || role === "radiologist") && room.faqs?.length) {
            setFaqs(room.faqs);
        }

  
        ws = new WebSocket(getWsUrl(room.id));
  
        ws.onopen = () => {
          console.log("✅ WebSocket connected");
          setSocketReady(true);
        };
  
        ws.onclose = () => {
          console.log("❌ WebSocket closed");
          setSocketReady(false);
          historyLoadedRef.current = false;
        };
  
        ws.onerror = (error) => {
          console.error("⚠️ WebSocket error:", error);
          setSocketReady(false);
        };
  
        ws.onmessage = (e) => {
          try {
            const data = JSON.parse(e.data);
            console.log("📨 Received:", data.type || "chat_message");
  
            // 🧠 HISTORY (replace, not append)
            if (data.type === "history") {
              console.log(`📜 Loaded ${data.messages?.length || 0} history messages`);
              historyLoadedRef.current = true;
              setMessages(data.messages || []);
              return;
            }
  
            // 🧠 LIVE MESSAGE - Check for duplicates
            if (historyLoadedRef.current) {
              setMessages(prev => {
                // Check if this message already exists (duplicate detection)
                const isDuplicate = prev.some(msg => 
                  msg.sender === data.sender && 
                  msg.message === data.message &&
                  (!msg.timestamp || !data.timestamp || msg.timestamp === data.timestamp)
                );
                
                if (isDuplicate) {
                  console.log("⚠️ Skipping duplicate message:", data.message);
                  return prev;
                }
                
                console.log("➕ Adding new message:", data.message);
                return [...prev, data];
              });
            } else {
              // If history hasn't loaded yet, just add the message
              console.log("➕ Adding message before history load:", data.message);
              setMessages(prev => [...prev, data]);
            }
          } catch (error) {
            console.error("Failed to parse message:", error);
          }
        };
  
        wsRef.current = ws;
      })
      .catch(error => {
        console.error("Failed to fetch active room:", error);
      });
  
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
        wsRef.current = null;
      }
      historyLoadedRef.current = false;
    };
  }, [role]);


  const openFile = async (fileKey) => {
    const res = await fetch(
      `${API_CHAT_URL}/presigned-url/?key=${encodeURIComponent(fileKey)}`,
      { credentials: "include" }
    );
    const data = await res.json();
    window.open(data.url, "_blank");
  };


  // ----------------------------------
  // SEND MESSAGE
  // ----------------------------------
  const sendMessage = () => {
    if (!socketReady || !text.trim()) return;
  
    if (wsRef.current && wsRef.current.readyState === WebSocket.OPEN) {
      console.log("📤 Sending message:", text);
      wsRef.current.send(JSON.stringify({ message: text }));
      setText("");
    } else {
      console.warn("WebSocket not ready");
      alert("Connection not ready. Please wait...");
    }
  };

  const toggleFaq = (index) => {
    setExpandedFaqs(prev => ({
      ...prev,
      [index]: !prev[index]   // toggle true/false
    }));
  };

  // ----------------------------------
  // FILE UPLOAD
  // ----------------------------------
  // const uploadFile = async (file) => {
  //   if (!file) return;
    
  //   if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
  //     alert("Chat is still connecting. Please wait.");
  //     return;
  //   }
  
  //   const formData = new FormData();
  //   formData.append("file", file);
  
  //   try {
  //     const res = await fetch(`${API_CHAT_URL}/upload/`, {
  //       method: "POST",
  //       credentials: "include",
  //       body: formData,
  //     });
  
  //     const data = await res.json();
  
  //     wsRef.current.send(
  //       JSON.stringify({
  //         message: "",
  //         file: data.file_url,
  //       })
  //     );
  //   } catch (error) {
  //     console.error("File upload failed:", error);
  //     alert("File upload failed. Please try again.");
  //   }
  // };

  const uploadFile = async (file) => {
    if (!file) return;
  
    if (!wsRef.current || wsRef.current.readyState !== WebSocket.OPEN) {
      alert("Chat is still connecting. Please wait.");
      return;
    }
  
    const formData = new FormData();
    formData.append("file", file);
    formData.append("room_id", roomIdRef.current); // 🔥 REQUIRED
  
    try {
      const res = await fetch(`${API_CHAT_URL}/upload/`, {
        method: "POST",
        credentials: "include",
        body: formData,
      });
  
      const data = await res.json();
  
      wsRef.current.send(
        JSON.stringify({
          message: "",
          file: data.file_key,
          message_id: data.message_id,
        })
      );
    } catch (error) {
      console.error("File upload failed:", error);
      alert("File upload failed. Please try again.");
    }
  };


  // ----------------------------------
  // HANDLE ENTER KEY
  // ----------------------------------
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && text.trim() && socketReady) {
      sendMessage();
    }
  };

  return (
    <div
      style={{
        position: "fixed",
        bottom: "90px",
        right: "20px",
        width: "350px",
        height: "450px",
        background: "#fff",
        borderRadius: "10px",
        boxShadow: "0 4px 20px rgba(0,0,0,0.15)",
        display: "flex",
        flexDirection: "column",
        zIndex: 9999,
        overflow: "hidden",
        fontFamily: "Arial, sans-serif"
      }}
    >
      {/* Header */}
      <div
        style={{
          padding: "15px",
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "#fff",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          borderBottom: "1px solid #e0e0e0"
        }}
      >
        <div>
          <span style={{ fontWeight: "bold", fontSize: "16px" }}>Live Support</span>
          <div style={{ fontSize: "11px", opacity: 0.8, marginTop: "2px" }}>
            {socketReady ? "🟢 Connected" : "🟡 Connecting..."}
          </div>
        </div>
        <button 
          onClick={onClose} 
          style={{ 
            background: "transparent", 
            border: "none", 
            color: "#fff", 
            fontSize: "20px",
            cursor: "pointer",
            padding: "0",
            width: "30px",
            height: "30px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
        >
          ✖
        </button>
      </div>

      {/* Messages Container */}
      <div
        style={{
          flex: 1,
          padding: "15px",
          overflowY: "auto",
          background: "#f8f9fa",
          display: "flex",
          flexDirection: "column",
          gap: "12px"
        }}
      >
        {/* FAQs */}
        {/* {faqs.length > 0 && (
          <div style={{ marginBottom: "12px" }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{ padding: "8px", background: "#fff3cd", borderRadius: "8px", border: "1px solid #ffeeba", marginBottom: "6px" }}>
                <div style={{ fontWeight: "bold" }}>❓ {faq.question}</div>
                <div style={{ marginTop: "4px" }}>💡 {faq.answer}</div>
              </div>
            ))}
          </div>
        )} */}
        {/* FAQs */}
        {faqs.length > 0 && (
          <div style={{ marginBottom: "12px" }}>
            {faqs.map((faq, i) => (
              <div key={i} style={{ marginBottom: "6px", borderRadius: "8px", border: "1px solid #ffeeba", background: "#fff3cd", cursor: "pointer" }}>
                
                {/* Question */}
                <div 
                  onClick={() => toggleFaq(i)}
                  style={{ padding: "8px", fontWeight: "bold", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                >
                  ❓ {faq.question}
                  <span>{expandedFaqs[i] ? "▲" : "▼"}</span>
                </div>
                
                {/* Answer (conditionally visible) */}
                {expandedFaqs[i] && (
                  <div style={{ padding: "8px", borderTop: "1px solid #ffeeba", background: "#fff8e1" }}>
                    💡 {faq.answer}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {messages.length === 0 ? (
          <div style={{ 
            textAlign: "center", 
            color: "#6c757d", 
            padding: "40px 20px",
            fontSize: "14px"
          }}>
            {socketReady ? "No messages yet. Start the conversation!" : "Connecting to chat..."}
          </div>
        ) : (
          messages.map((m, i) => {
            // Check if this is a duplicate to skip rendering
            const isDuplicate = i > 0 && 
              messages[i-1].sender === m.sender && 
              messages[i-1].message === m.message;
            
            if (isDuplicate) return null;
            
            const isCurrentUser = m.sender === "Bot" || m.sender === role;
            
            return (
              <div 
                key={i} 
                style={{ 
                  maxWidth: "80%",
                  alignSelf: isCurrentUser ? "flex-end" : "flex-start",
                  animation: "fadeIn 0.3s ease-in"
                }}
              >
                <div style={{ 
                  display: "flex", 
                  flexDirection: "column",
                  alignItems: isCurrentUser ? "flex-end" : "flex-start"
                }}>
                  <div style={{ 
                    fontSize: "11px", 
                    color: "#6c757d", 
                    marginBottom: "2px",
                    padding: "0 8px"
                  }}>
                    {m.sender}
                    {m.timestamp && (
                      <span style={{ marginLeft: "8px" }}>
                        {new Date(m.timestamp).toLocaleTimeString([], { 
                          hour: '2-digit', 
                          minute: '2-digit' 
                        })}
                      </span>
                    )}
                  </div>
                  <div style={{ 
                    padding: "10px 14px",
                    background: isCurrentUser ? "#e3f2fd" : "#fff",
                    borderRadius: isCurrentUser ? "18px 18px 4px 18px" : "18px 18px 18px 4px",
                    boxShadow: "0 1px 2px rgba(0,0,0,0.1)",
                    border: `1px solid ${isCurrentUser ? "#bbdefb" : "#e0e0e0"}`,
                    wordBreak: "break-word"
                  }}>
                    <div>{m.message}</div>
                    {/* {m.file && (
                      <div style={{ marginTop: "8px" }}>
                        <a 
                          href={m.file} 
                          target="_blank" 
                          rel="noreferrer"
                          style={{ 
                            color: "#2196f3", 
                            textDecoration: "none",
                            fontSize: "13px",
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "6px",
                            padding: "4px 8px",
                            background: "rgba(33, 150, 243, 0.1)",
                            borderRadius: "4px",
                            border: "1px solid rgba(33, 150, 243, 0.2)"
                          }}
                        >
                          <span>📎</span> Download File
                        </a>
                      </div>
                    )} */}
                    {m.file_key || m.file && (
                      <button
                        onClick={() => openFile(m.file_key || m.file)}
                        style={{
                          background: "none",
                          border: "none",
                          color: "#2196f3",
                          cursor: "pointer",
                          fontSize: "13px"
                        }}
                      >
                        📎 Download File
                      </button>
                    )}

                  </div>
                </div>
              </div>
            );
          })
        )}
        <div ref={messageEndRef} />
      </div>

      {/* Input Area */}
      <div style={{ 
        padding: "12px 15px", 
        borderTop: "1px solid #e0e0e0",
        background: "#fff"
      }}>
        {/* File Upload */}
        <div style={{ marginBottom: "10px" }}>
          <label style={{
            display: "inline-flex",
            alignItems: "center",
            gap: "8px",
            padding: "6px 12px",
            background: "#f8f9fa",
            border: "1px solid #dee2e6",
            borderRadius: "6px",
            fontSize: "13px",
            cursor: socketReady ? "pointer" : "not-allowed",
            opacity: socketReady ? 1 : 0.5
          }}>
            <span>📎</span>
            <span>Choose File</span>
            <input
              type="file"
              onChange={(e) => uploadFile(e.target.files[0])}
              style={{ display: "none" }}
              disabled={!socketReady}
            />
          </label>
        </div>
        
        {/* Message Input */}
        <div style={{ display: "flex", gap: "8px" }}>
          <input
            value={text}
            disabled={!socketReady}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={socketReady ? "Type your message..." : "Connecting to chat..."}
            style={{ 
              flex: 1,
              padding: "10px 14px",
              border: `1px solid ${socketReady ? "#dee2e6" : "#e9ecef"}`,
              borderRadius: "8px",
              fontSize: "14px",
              outline: "none",
              transition: "all 0.2s",
              background: socketReady ? "#fff" : "#f8f9fa",
              color: socketReady ? "#212529" : "#6c757d"
            }}
          />
          
          <button 
            onClick={sendMessage} 
            disabled={!socketReady || !text.trim()}
            style={{ 
              padding: "10px 20px",
              background: socketReady && text.trim() 
                ? "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" 
                : "#e9ecef",
              color: socketReady && text.trim() ? "#fff" : "#6c757d",
              border: "none",
              borderRadius: "8px",
              cursor: socketReady && text.trim() ? "pointer" : "not-allowed",
              fontWeight: "bold",
              fontSize: "14px",
              transition: "all 0.2s",
              minWidth: "80px"
            }}
          >
            {socketReady ? "Send" : "..."}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(5px); }
          to { opacity: 1; transform: translateY(0); }
        }
        
        /* Custom scrollbar */
        div::-webkit-scrollbar {
          width: 6px;
        }
        
        div::-webkit-scrollbar-track {
          background: #f1f1f1;
          border-radius: 3px;
        }
        
        div::-webkit-scrollbar-thumb {
          background: #c1c1c1;
          border-radius: 3px;
        }
        
        div::-webkit-scrollbar-thumb:hover {
          background: #a8a8a8;
        }
      `}</style>
    </div>
  );
}